import type {
  DirectorEmailTemplate, EmailTemplatesResponse, LeagueEmailContextResponse,
  MessageListResponse, MessageRecipientsResponse,
} from '@league/shared/contract';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui';
import { apiFetch } from '../../lib/api';
import { leagueDate, leagueTime } from '../../lib/when';
import { DirectorNoteField, EmailPreviewFrame } from './EmailPieces';
import { useLeagueEmailSend } from './useLeagueEmailSend';

const SCOPED = ['reminder', 'results', 'final_results', 'seating'];
const KINDS = ['welcome', 'kickoff', ...SCOPED];
const UUID = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;
const linkStyle = 'underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4';

export function emailPreviewLabels(template: DirectorEmailTemplate) {
  switch (template.kind) {
    case 'seating': return { heading: `Session ${template.sessionNumber} seating notice`, button: 'Send seating notice' };
    case 'reminder': return { heading: `Session ${template.sessionNumber} reminder`, button: 'Send session reminder' };
    case 'results': return { heading: `Session ${template.sessionNumber} update`, button: 'Send session update' };
    case 'final_results': return { heading: 'Final standings', button: 'Send final standings' };
    default: return { heading: 'Welcome to the league', button: 'Send welcome email' };
  }
}

export function emailPreviewContextValid(kind: string, sessionId: string | null) {
  return KINDS.includes(kind) && (SCOPED.includes(kind) ? Boolean(sessionId && UUID.test(sessionId)) : sessionId === null);
}

/** A route outside LeagueWorkspace: the application shell stays, the league tabs do not. */
export function FocusedEmailPreview() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const kind = params.get('template') ?? '';
  const sessionId = params.get('sessionId');
  // Remount transient send state when browser navigation changes the selected message.
  return <EmailPreview key={`${id}:${kind}:${sessionId}`} leagueId={id} kind={kind} sessionId={sessionId} />;
}

export function EmailPreview({ leagueId, kind, sessionId }: { leagueId: string; kind: string; sessionId: string | null }) {
  const client = useQueryClient();
  const valid = emailPreviewContextValid(kind, sessionId);
  const league = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => apiFetch<LeagueEmailContextResponse>(`/api/leagues/${leagueId}`),
    enabled: valid,
  });
  const templates = useQuery({
    queryKey: ['email-templates', leagueId, sessionId],
    queryFn: () => apiFetch<EmailTemplatesResponse>(`/api/leagues/${leagueId}/email-templates${sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : ''}`),
    enabled: valid,
  });
  const recipients = useQuery({
    queryKey: ['message-recipients', leagueId, kind, sessionId],
    queryFn: () => apiFetch<MessageRecipientsResponse>(`/api/leagues/${leagueId}/message-recipients?templateType=${encodeURIComponent(kind)}${sessionId ? `&sessionId=${encodeURIComponent(sessionId)}` : ''}`),
    enabled: valid,
  });
  const history = useQuery({
    queryKey: ['messages', leagueId],
    queryFn: () => apiFetch<MessageListResponse>(`/api/leagues/${leagueId}/messages`),
    enabled: valid,
    // Sending queues work. Follow its real status rather than declaring delivery on POST success.
    refetchInterval: (query) => query.state.data?.messages.some((m) =>
      m.templateType === kind && m.sessionId === sessionId && ['pending', 'scheduled'].includes(m.status)) ? 3000 : false,
  });
  const send = useLeagueEmailSend(leagueId);
  const [note, setNote] = useState('');
  const template = templates.data?.templates.find((row) => row.kind === kind && row.sessionId === sessionId);
  const session = league.data?.sessions.find((row) => row.id === sessionId && row.status !== 'voided');
  const people = recipients.data?.recipients ?? [];
  const messages = (history.data?.messages ?? []).filter((m) =>
    m.templateType === kind && m.sessionId === sessionId && (kind !== 'seating' || m.seatingVersion === template?.seatingVersion) && people.some((p) => p.id === m.playerId));
  const accepted = messages.filter((m) => ['sent', 'delivered'].includes(m.status) && m.sentAt && m.providerMessageId).length;
  const queued = messages.filter((m) => ['pending', 'scheduled'].includes(m.status)).length;
  const failed = messages.filter((m) => ['failed', 'bounced'].includes(m.status)).length;
  const retryable = messages.some((m) => m.status === 'failed' && !m.sentAt && !m.providerMessageId);
  const error = [league, templates, recipients, history].some((q) => q.isError);
  const loading = [league, templates, recipients, history].some((q) => q.isPending);
  const unavailable = !valid || (!loading && (!template || (SCOPED.includes(kind) && !session)));
  const back = () => { void client.invalidateQueries({ queryKey: ['director-next-steps'] }); };

  return (
    <div className="mx-auto w-full min-w-0 max-w-3xl px-4 py-8 sm:px-8">
      <Link className={`text-sm text-ink-2 ${linkStyle}`} to="/director" onClick={back}>← Back to Dashboard</Link>
      {unavailable || error ? (
        <div className="mt-8" role="alert">
          <h1 className="font-display text-3xl">This email preview is unavailable</h1>
          <p className="mt-3 leading-relaxed">Return to the Dashboard and select the message for the session you want. No email has been sent by opening this preview.</p>
        </div>
      ) : loading || !template || !league.data ? <p className="mt-8" role="status">Loading email preview…</p> : (
        <>
          <h1 className="mt-7 font-display text-3xl leading-tight sm:text-4xl">{emailPreviewLabels(template).heading}</h1>
          <p className="mt-3 text-base text-ink-2">{league.data.league.name}</p>
          {session ? <p className="mt-1 text-sm text-ink-2">{leagueDate(session.startsAt, league.data.league.timeZone, { month: 'long', day: 'numeric', year: 'numeric' })} · {leagueTime(session.startsAt, league.data.league.timeZone)}</p> : null}
          <p className="mt-4 text-sm text-ink-2"><Link className={linkStyle} to={`/director/leagues/${leagueId}/emails`}>View all league emails</Link></p>
          <div className="my-6 text-sm">
            <p>{people.length} eligible {people.length === 1 ? 'recipient' : 'recipients'}</p>
            <details className="mt-2">
              <summary className={`w-fit cursor-pointer ${linkStyle}`}>View recipients</summary>
              <ul className="mt-3 max-h-48 overflow-y-auto break-words leading-relaxed text-ink-2">
                {people.map((person) => <li key={person.id}>{person.email}</li>)}
              </ul>
            </details>
          </div>
          {kind === 'seating' ? <p className="mb-3 text-sm text-ink-2">Example for one recipient. Each player receives their own table and tablemates.</p> : null}
          {template.html ? (
            <>
              <p className="mb-2 text-sm text-ink-2"><span className="font-medium text-ink">Subject:</span> {template.subject}</p>
              <EmailPreviewFrame html={template.html} title="Email preview" />
              <div className="mt-5 max-w-xl"><DirectorNoteField id="email-note" value={note} onChange={setNote} /></div>
            </>
          ) : (
            <article aria-label="Email preview" className="rounded-md border border-light bg-paper p-5 sm:p-8">
              <h2 className="break-words text-lg font-medium leading-relaxed">{template.subject}</h2>
              <div className="mt-6 whitespace-pre-wrap break-words text-base leading-relaxed text-ink">{template.body}</div>
            </article>
          )}
          <div className="mt-5 text-sm leading-relaxed text-ink-2" role="status" aria-live="polite">
            {accepted > 0 ? <p>Already sent to {accepted}. Provider acceptance does not guarantee delivery.</p> : null}
            {queued > 0 ? <p>{queued} queued; sending is still in progress.</p> : null}
            {failed > 0 ? <p>{failed} failed or bounced. {accepted > 0 ? 'Only part of this message was sent successfully.' : 'Sending is not complete.'} Review details in all league emails.</p> : null}
            {send.isSuccess ? <p>{send.data.send.queued > 0 ? `Request accepted: ${send.data.send.queued} queued for sending. See the recorded status above for progress.` : 'No new emails were queued. Existing message records prevented duplicate sending.'}</p> : null}
          </div>
          {send.isError ? <p className="mt-4 text-sm text-signal-d" role="alert">{kind === 'seating' ? 'The request could not be confirmed. Refresh this preview to check the current arrangement before retrying.' : 'We could not confirm the send request. Review the message status or retry; existing duplicate protection still applies.'}</p> : null}
          <div className="mt-6 flex flex-wrap items-center gap-5">
            {!send.isSuccess || retryable ? <Button variant="primary" className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink" disabled={send.isPending || people.length === 0} onClick={() => send.mutate({ template, note })}>{send.isPending ? 'Queueing…' : emailPreviewLabels(template).button}</Button> : null}
            <Link className={`text-sm ${linkStyle}`} to="/director" onClick={back}>{send.isSuccess ? 'Return to Dashboard' : 'Back without sending'}</Link>
          </div>
        </>
      )}
    </div>
  );
}
